/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(() => {
  if (window.customElements.get("collection-infinite-scroll")) {
    return;
  }

  class CollectionInfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.abortController = null;
      this.loadMoreButton = this.querySelector("[data-load-more]");
      this.productFacet = this.closest("product-facet");

      this.loadMoreButton?.addEventListener("click", this._loadNextPage.bind(this));

      if (!this.nextUrl || this.hasAttribute("complete")) {
        this._markComplete();
        return;
      }

      if ("IntersectionObserver" in window) {
        this.intersectionObserver = new IntersectionObserver(this._onIntersection.bind(this), {
          rootMargin: "600px 0px 600px 0px"
        });
        this.intersectionObserver.observe(this);
      } else {
        this._enableManualLoading();
      }
    }

    disconnectedCallback() {
      this.intersectionObserver?.disconnect();
      this.abortController?.abort();
    }

    get nextUrl() {
      return this.getAttribute("data-next-url");
    }

    get sectionId() {
      return this.getAttribute("data-section-id");
    }

    async _onIntersection(entries) {
      if (entries.some((entry) => entry.isIntersecting)) {
        await this._loadNextPage();
      }
    }

    async _loadNextPage() {
      if (this.loading || !this.nextUrl) {
        return;
      }

      this.loading = true;
      this.setAttribute("aria-busy", "true");
      this._triggerThemeEvent("theme:loading:start");

      try {
        this.abortController = new AbortController();
        const nextPageUrl = new URL(this.nextUrl, window.location.origin);
        nextPageUrl.searchParams.set("section_id", this.sectionId);

        const response = await fetch(nextPageUrl.toString(), {
          signal: this.abortController.signal
        });
        const responseText = await response.text();
        const nextPage = new DOMParser().parseFromString(responseText, "text/html");
        const nextProductList = nextPage.querySelector("#facet-main .product-list__inner");
        const currentProductList = this.productFacet?.querySelector("#facet-main .product-list__inner");

        if (!nextProductList || !currentProductList) {
          this._enableManualLoading();
          return;
        }

        const nextProducts = Array.from(nextProductList.children);

        nextProducts.forEach((productItem) => {
          productItem.removeAttribute("reveal");
          productItem.style.opacity = "";
          currentProductList.appendChild(document.importNode(productItem, true));
        });

        const productList = this.productFacet?.querySelector("product-list");

        if (productList && Array.isArray(productList.productItems)) {
          productList.productItems = Array.from(productList.querySelectorAll("product-item"));
        }

        const nextInfiniteScroll = nextPage.querySelector("collection-infinite-scroll");

        if (nextInfiniteScroll?.dataset.nextUrl) {
          this.dataset.nextUrl = nextInfiniteScroll.dataset.nextUrl;
          this.dataset.currentPage = nextInfiniteScroll.dataset.currentPage;
          this.dataset.totalPages = nextInfiniteScroll.dataset.totalPages;
        } else {
          this._markComplete();
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          this._enableManualLoading();
        }
      } finally {
        this.loading = false;
        this.removeAttribute("aria-busy");
        this._triggerThemeEvent("theme:loading:end");
      }
    }

    _enableManualLoading() {
      this.intersectionObserver?.disconnect();
      this.setAttribute("manual", "");
      this.loadMoreButton?.removeAttribute("hidden");
    }

    _markComplete() {
      this.intersectionObserver?.disconnect();
      this.removeAttribute("data-next-url");
      this.loadMoreButton?.setAttribute("hidden", "");
      this.remove();
    }

    _triggerThemeEvent(eventName) {
      document.documentElement.dispatchEvent(new CustomEvent(eventName, {
        bubbles: true
      }));
    }
  }

  window.customElements.define("collection-infinite-scroll", CollectionInfiniteScroll);
})();

(() => {
  if (window.customElements.get("product-card-swatch-carousel")) {
    return;
  }

  class ProductCardSwatchCarousel extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector(".product-item-meta__swatch-scroller");

      if (!this.scroller) {
        return;
      }

      this._boundUpdate = this._update.bind(this);

      this.scroller.addEventListener("scroll", this._boundUpdate, { passive: true });
      window.addEventListener("resize", this._boundUpdate);

      requestAnimationFrame(() => {
        this._scrollSelectedIntoView();
        this._update();
      });
    }

    disconnectedCallback() {
      this.scroller?.removeEventListener("scroll", this._boundUpdate);
      window.removeEventListener("resize", this._boundUpdate);
    }

    _scrollSelectedIntoView() {
      const selectedSwatch = this.querySelector(".color-swatch__radio:checked")?.closest(".color-swatch");

      if (!selectedSwatch) {
        return;
      }

      const swatchStart = selectedSwatch.offsetLeft;
      const swatchEnd = swatchStart + selectedSwatch.offsetWidth;
      const viewportStart = this.scroller.scrollLeft;
      const viewportEnd = viewportStart + this.scroller.clientWidth;

      if (swatchStart < viewportStart) {
        this.scroller.scrollLeft = swatchStart;
      } else if (swatchEnd > viewportEnd) {
        this.scroller.scrollLeft = swatchEnd - this.scroller.clientWidth;
      }
    }

    _update() {
      const maxScroll = Math.max(this.scroller.scrollWidth - this.scroller.clientWidth, 0);
      const currentScroll = Math.abs(this.scroller.scrollLeft);

      this.classList.toggle("is-scrollable", maxScroll > 1);
      this.classList.toggle("is-at-start", currentScroll <= 1);
      this.classList.toggle("is-at-end", currentScroll >= maxScroll - 1);
    }
  }

  window.customElements.define("product-card-swatch-carousel", ProductCardSwatchCarousel);
})();

(() => {
  if (window.customElements.get("collection-description-toggle")) {
    return;
  }

  class CollectionDescriptionToggle extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector("[data-collection-description-content]");
      this.button = this.querySelector("[data-collection-description-toggle]");
      this.moreText = this.querySelector("[data-see-more-text]");
      this.lessText = this.querySelector("[data-see-less-text]");

      if (!this.content || !this.button) {
        return;
      }

      this._boundToggle = this._toggle.bind(this);
      this._boundUpdate = this._updateOverflow.bind(this);

      this.button.addEventListener("click", this._boundToggle);
      window.addEventListener("resize", this._boundUpdate);

      requestAnimationFrame(this._boundUpdate);

      if (document.fonts?.ready) {
        document.fonts.ready.then(this._boundUpdate);
      }
    }

    disconnectedCallback() {
      this.button?.removeEventListener("click", this._boundToggle);
      window.removeEventListener("resize", this._boundUpdate);
    }

    _toggle() {
      const isExpanded = this.hasAttribute("expanded");

      this.toggleAttribute("expanded", !isExpanded);
      this.button.setAttribute("aria-expanded", !isExpanded ? "true" : "false");
      this.moreText?.toggleAttribute("hidden", !isExpanded);
      this.lessText?.toggleAttribute("hidden", isExpanded);
    }

    _updateOverflow() {
      const computedStyle = window.getComputedStyle(this.content);
      const lineHeight = parseFloat(computedStyle.lineHeight);
      const clampLines = parseInt(computedStyle.getPropertyValue("--fv-collection-description-line-clamp"), 10) || 2;

      if (!Number.isFinite(lineHeight)) {
        return;
      }

      const wasExpanded = this.hasAttribute("expanded");

      if (!wasExpanded) {
        this.setAttribute("expanded", "");
      }

      const maxCollapsedHeight = lineHeight * clampLines;
      const isOverflowing = this.content.scrollHeight > maxCollapsedHeight + 1;

      if (!wasExpanded) {
        this.removeAttribute("expanded");
      }

      this.toggleAttribute("overflowing", isOverflowing);
      this.button.toggleAttribute("hidden", !isOverflowing);

      if (!isOverflowing) {
        this.removeAttribute("expanded");
        this.button.setAttribute("aria-expanded", "false");
        this.moreText?.removeAttribute("hidden");
        this.lessText?.setAttribute("hidden", "");
      }
    }
  }

  window.customElements.define("collection-description-toggle", CollectionDescriptionToggle);
})();
